import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
} from "react-bootstrap";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";
import LotusIcon from "../components/LotusIcon";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");

  const { login, isAuthenticated, loading } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated && !loading) {
    return <Navigate to="/" />;
  }

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }

    // Clear login error when user changes input
    if (loginError) {
      setLoginError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoginError("");

    try {
      await login(formData.email, formData.password);
      // Redirect is handled by the auth context
    } catch (err) {
      setLoginError(
        err.response?.data?.message ||
          "Login failed. Please check your credentials and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Row
        className="justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <Col md={6} lg={5}>
          <Card
            className="shadow border-0"
            style={{ borderRadius: "15px", overflow: "hidden" }}
          >
            <div
              className="text-center p-4"
              style={{
                background: "linear-gradient(to right, #fff0f5, #fff8dc)",
              }}
            >
              <LotusIcon size={64} className="mb-3" />
              <h2 style={{ color: "#d9669b", fontWeight: "600" }}>Lotus</h2>
              <p className="text-muted">Sign in to continue to your account</p>
            </div>
            <Card.Body className="p-4">
              {loginError && <Alert variant="danger">{loginError}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="me-2"
                      style={{ color: "#d9669b" }}
                    />
                    Email address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    isInvalid={!!errors.email}
                    className="border-0 shadow-sm"
                    style={{ backgroundColor: "#fafafa", padding: "0.8rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4" controlId="password">
                  <Form.Label>
                    <FontAwesomeIcon
                      icon={faLock}
                      className="me-2"
                      style={{ color: "#d9669b" }}
                    />
                    Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    isInvalid={!!errors.password}
                    className="border-0 shadow-sm"
                    style={{ backgroundColor: "#fafafa", padding: "0.8rem" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                    className="py-2"
                    style={{
                      background: "linear-gradient(to right, #e66b8f, #d4af37)",
                      border: "none",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {isSubmitting ? "Signing in..." : "Sign In"}
                  </Button>
                </div>
              </Form>

              <div className="text-center mt-4">
                <p className="mb-0">
                  Don't have an account?{" "}
                  <Link
                    to="/register"
                    style={{
                      color: "#d9669b",
                      textDecoration: "none",
                      fontWeight: "600",
                    }}
                  >
                    Register
                  </Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
